import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  type AlertColor,
  type ButtonProps,
} from "@mui/material";
import type { FC, ReactNode } from "react";

import { displayFontFamily } from "../theme/mtgTheme";

export type AsyncWorkflowStageVisualState =
  | "complete"
  | "current"
  | "locked"
  | "failed";

export interface AsyncWorkflowAction {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  color?: ButtonProps["color"];
  variant?: ButtonProps["variant"];
}

export interface AsyncWorkflowStageDefinition {
  id: string;
  label: string;
  summary: string;
  state: AsyncWorkflowStageVisualState;
}

const STAGE_STATE_STYLE: Record<
  AsyncWorkflowStageVisualState,
  {
    label: string;
    chipColor: "default" | "primary" | "success" | "warning" | "error";
    accentColor: string;
  }
> = {
  complete: {
    label: "Complete",
    chipColor: "success",
    accentColor: "success.main",
  },
  current: {
    label: "Current",
    chipColor: "primary",
    accentColor: "primary.main",
  },
  locked: {
    label: "Locked",
    chipColor: "default",
    accentColor: "divider",
  },
  failed: {
    label: "Failed",
    chipColor: "error",
    accentColor: "error.main",
  },
};

const CardSectionHeader = ({
  title,
  description,
  badge,
}: {
  title: string;
  description?: string;
  badge?: ReactNode;
}) => (
  <Stack
    direction={{ xs: "column", md: "row" }}
    spacing={1.5}
    justifyContent="space-between"
    alignItems={{ xs: "flex-start", md: "center" }}
  >
    <Box>
      <Typography variant="h5" sx={{ fontFamily: displayFontFamily }}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      ) : null}
    </Box>
    {badge}
  </Stack>
);

export const WorkflowHeader: FC<{
  title: string;
  description: string;
  stageLabel: string;
  healthLabel: string;
  healthColor: AlertColor;
  nextActionTitle: string;
  nextActionDescription: string;
  primaryAction?: AsyncWorkflowAction;
  secondaryActions?: readonly AsyncWorkflowAction[];
}> = ({
  title,
  description,
  stageLabel,
  healthLabel,
  healthColor,
  nextActionTitle,
  nextActionDescription,
  primaryAction,
  secondaryActions = [],
}) => (
  <Card
    sx={{
      borderRadius: 3,
      background:
        "linear-gradient(135deg, rgba(26,32,43,0.96) 0%, rgba(17,24,39,0.92) 100%)",
      color: "common.white",
      border: "1px solid",
      borderColor: "divider",
    }}
  >
    <CardContent sx={{ p: { xs: 3, lg: 4 } }}>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={3}
          justifyContent="space-between"
        >
          <Box sx={{ maxWidth: 760 }}>
            <Typography
              variant="h3"
              sx={{ fontFamily: displayFontFamily, mb: 1 }}
            >
              {title}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "rgba(255,255,255,0.78)", maxWidth: 680 }}
            >
              {description}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 2 }}
            >
              <Chip
                label={`Stage: ${stageLabel}`}
                color="primary"
                variant="filled"
                size="small"
              />
              <Chip
                label={healthLabel}
                color={healthColor === "warning" ? "warning" : healthColor}
                variant="outlined"
                size="small"
                sx={{
                  color: "common.white",
                  borderColor: "rgba(255,255,255,0.3)",
                }}
              />
            </Stack>
          </Box>

          <Card
            variant="outlined"
            sx={{
              minWidth: { xs: "100%", lg: 340 },
              borderColor: "rgba(255,255,255,0.18)",
              bgcolor: "rgba(255,255,255,0.04)",
            }}
          >
            <CardContent>
              <Typography
                variant="overline"
                sx={{ color: "rgba(255,255,255,0.72)" }}
              >
                Next Action
              </Typography>
              <Typography
                variant="h6"
                sx={{ mt: 1, fontFamily: displayFontFamily }}
              >
                {nextActionTitle}
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 1.5, color: "rgba(255,255,255,0.78)" }}
              >
                {nextActionDescription}
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ mt: 2.5 }}
              >
                {primaryAction ? (
                  <Button
                    variant={primaryAction.variant ?? "contained"}
                    color={primaryAction.color ?? "primary"}
                    onClick={primaryAction.onClick}
                    disabled={primaryAction.disabled || primaryAction.loading}
                    startIcon={
                      primaryAction.loading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                  >
                    {primaryAction.label}
                  </Button>
                ) : null}
                {secondaryActions.map((action) => (
                  <Button
                    key={action.label}
                    variant={action.variant ?? "outlined"}
                    color={action.color ?? "inherit"}
                    onClick={action.onClick}
                    disabled={action.disabled || action.loading}
                    startIcon={
                      action.loading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                    sx={
                      action.color === "inherit"
                        ? {
                            color: "common.white",
                            borderColor: "rgba(255,255,255,0.24)",
                          }
                        : undefined
                    }
                  >
                    {action.label}
                  </Button>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

export const WorkflowStageRail: FC<{
  stages: readonly AsyncWorkflowStageDefinition[];
  onSelectStage?: (stageId: string) => void;
}> = ({ stages, onSelectStage }) => (
  <Card
    sx={{
      position: { lg: "sticky" },
      top: { lg: 88 },
      borderRadius: 3,
      display: { xs: "none", lg: "block" },
    }}
  >
    <CardContent>
      <Stack spacing={1.5}>
        <Typography variant="overline" color="text.secondary">
          Workflow Stages
        </Typography>
        {stages.map((stage, index) => {
          const style = STAGE_STATE_STYLE[stage.state];

          return (
            <Box
              key={stage.id}
              component="button"
              type="button"
              onClick={() => onSelectStage?.(stage.id)}
              sx={{
                appearance: "none",
                textAlign: "left",
                border: "1px solid",
                borderColor:
                  stage.state === "current"
                    ? "primary.main"
                    : stage.state === "failed"
                      ? "error.main"
                      : "divider",
                borderRadius: 2,
                bgcolor:
                  stage.state === "current"
                    ? "action.selected"
                    : "background.paper",
                color: "text.primary",
                px: 1.5,
                py: 1.25,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: "inherit",
              }}
            >
              <Stack spacing={0.75}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 700 }}
                  >
                    {index + 1}
                  </Typography>
                  <Chip
                    size="small"
                    label={style.label}
                    color={style.chipColor}
                    variant={stage.state === "current" ? "filled" : "outlined"}
                  />
                </Stack>
                <Typography variant="subtitle2">{stage.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {stage.summary}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </CardContent>
  </Card>
);

export const WorkflowStageCard: FC<{
  id: string;
  title: string;
  description: string;
  state: AsyncWorkflowStageVisualState;
  summary: string;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}> = ({
  id,
  title,
  description,
  state,
  summary,
  expanded,
  onToggle,
  children,
}) => {
  const style = STAGE_STATE_STYLE[state];

  return (
    <Card
      id={id}
      sx={{
        borderRadius: 3,
        borderLeft: "6px solid",
        borderLeftColor: style.accentColor,
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <CardSectionHeader
                title={title}
                description={description}
                badge={
                  <Chip
                    size="small"
                    label={style.label}
                    color={style.chipColor}
                    variant={state === "current" ? "filled" : "outlined"}
                  />
                }
              />
              <Button size="small" variant="text" onClick={onToggle}>
                {expanded ? "Hide details" : "Show details"}
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {summary}
            </Typography>
          </Stack>
        </Box>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider />
          <Box sx={{ p: 3 }}>{children}</Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export const WarningSummary: FC<{
  warningCount: number;
  latestWarningAt?: string;
  topCodes: readonly string[];
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}> = ({
  warningCount,
  latestWarningAt,
  topCodes,
  expanded,
  onToggle,
  children,
}) => (
  <Card sx={{ borderRadius: 3 }}>
    <CardContent sx={{ p: 0 }}>
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <CardSectionHeader
            title="Warning Summary"
            description="Persisted non-fatal workflow warnings are summarized here and only expanded when you need details."
            badge={
              <Chip
                label={`Warnings: ${warningCount}`}
                color={warningCount > 0 ? "warning" : "success"}
                variant={warningCount > 0 ? "filled" : "outlined"}
                size="small"
              />
            }
          />
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                {warningCount > 0
                  ? latestWarningAt
                    ? `Latest warning recorded ${new Date(latestWarningAt).toLocaleString()}.`
                    : "Warnings have been recorded for this workflow."
                  : "No warnings have been recorded for this workflow."}
              </Typography>
              {topCodes.length > 0 ? (
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 1.5 }}
                >
                  {topCodes.map((code) => (
                    <Chip
                      key={code}
                      label={code}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              ) : null}
            </Box>
            <Button variant="outlined" onClick={onToggle}>
              {expanded ? "Hide Warnings" : "View All Warnings"}
            </Button>
          </Stack>
        </Stack>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ p: 3 }}>{children}</Box>
      </Collapse>
    </CardContent>
  </Card>
);

export const DiagnosticsAccordion: FC<{
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}> = ({ expanded, onToggle, children }) => (
  <Card sx={{ borderRadius: 3 }}>
    <CardContent sx={{ p: 0 }}>
      <Box sx={{ p: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <CardSectionHeader
            title="Diagnostics"
            description="Technical counters, raw run state, and storage outputs live here so the main workflow stays focused on the next operator action."
          />
          <Button variant="outlined" onClick={onToggle}>
            {expanded ? "Hide Diagnostics" : "Show Diagnostics"}
          </Button>
        </Stack>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ p: 3 }}>{children}</Box>
      </Collapse>
    </CardContent>
  </Card>
);

export const RecoverySection: FC<{
  title: string;
  description: string;
  actions: readonly AsyncWorkflowAction[];
  children?: ReactNode;
}> = ({ title, description, actions, children }) => (
  <Card
    sx={{
      borderRadius: 3,
      borderColor: "warning.main",
      borderWidth: 1,
      borderStyle: "solid",
    }}
  >
    <CardContent>
      <Stack spacing={2}>
        <CardSectionHeader title={title} description={description} />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          flexWrap="wrap"
          useFlexGap
        >
          {actions.map((action) => (
            <Button
              key={action.label}
              variant={action.variant ?? "outlined"}
              color={action.color ?? "warning"}
              onClick={action.onClick}
              disabled={action.disabled || action.loading}
              startIcon={
                action.loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {action.label}
            </Button>
          ))}
        </Stack>
        {children}
      </Stack>
    </CardContent>
  </Card>
);
